// src/utils/analytics.js
// ─────────────────────────────────────────────────────────────────
// Google Analytics 4 — SJ Aroma Candles
// Usage: import { trackEvent, trackPageView } from '../utils/analytics';
// ─────────────────────────────────────────────────────────────────

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const isProduction = process.env.NODE_ENV === "production";

// ── Initialize GA4 ───────────────────────────────────────────────
export const initGA = () => {
  if (!GA_MEASUREMENT_ID || !isProduction) return;
  if (window.gtag) return; // prevent duplicate injection

  // Inject gtag script
  const script1 = document.createElement("script");
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script1);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false, // We send manually for SPA
  });
};

// ── Page View ────────────────────────────────────────────────────
export const trackPageView = (path, title) => {
  if (!window.gtag || !isProduction) return;
  window.gtag("event", "page_view", {
    page_path:  path,
    page_title: title,
  });
};

// ── Generic Event ────────────────────────────────────────────────
export const trackEvent = (eventName, params = {}) => {
  if (!window.gtag || !isProduction) return;
  window.gtag("event", eventName, params);
};

// ── Ecommerce Events ─────────────────────────────────────────────

// Product viewed
export const trackViewItem = (product) => {
  trackEvent("view_item", {
    currency: "INR",
    value: parseFloat(product.price),
    items: [{
      item_id:       String(product.id),
      item_name:     product.name,
      item_category: product.category?.name || "",
      price:         parseFloat(product.price),
      quantity:      1,
    }],
  });
};

// Product added to cart
export const trackAddToCart = (product, quantity = 1) => {
  trackEvent("add_to_cart", {
    currency: "INR",
    value: parseFloat(product.price) * quantity,
    items: [{
      item_id:       String(product.id),
      item_name:     product.name,
      item_category: product.category?.name || "",
      price:         parseFloat(product.price),
      quantity,
    }],
  });
};

// Checkout started
export const trackBeginCheckout = (cartItems, total) => {
  trackEvent("begin_checkout", {
    currency: "INR",
    value: parseFloat(total),
    items: cartItems.map(item => ({
      item_id:       String(item.product?.id || item.id),
      item_name:     item.product?.name || item.name,
      item_category: item.product?.category?.name || "",
      price:         parseFloat(item.product?.price || item.price),
      quantity:      item.quantity,
    })),
  });
};

// Purchase completed
export const trackPurchase = (order) => {
  trackEvent("purchase", {
    transaction_id: String(order.id),
    currency:       "INR",
    value:          parseFloat(order.total_price),
    items: (order.items || []).map(item => ({
      item_id:   String(item.product_id || item.id),
      item_name: item.product_name || item.name,
      price:     parseFloat(item.price_at_purchase),
      quantity:  item.quantity,
    })),
  });
};

// Search performed
export const trackSearch = (searchTerm) => {
  trackEvent("search", { search_term: searchTerm });
};

// User login
export const trackLogin = (method = "email") => {
  trackEvent("login", { method });
};

// User signup
export const trackSignUp = (method = "email") => {
  trackEvent("sign_up", { method });
};

// Customization form submitted
export const trackCustomizationRequest = () => {
  trackEvent("customization_request", {
    event_category: "engagement",
    event_label:    "Customization Form Submitted",
  });
};